
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { PlusIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline'
import { toast } from 'sonner'

import { selectUser } from '@/store/slices/authSlice'
import { 
  useGetWorkOrdersQuery, 
  useCreateWorkOrderMutation,
  useUpdateWorkOrderMutation,
  useDeleteWorkOrderMutation,
  useGetEquipmentQuery,
  useGetUsersQuery
} from '@/store/api'
import { WorkOrder } from '@/types'

import WorkOrderList from '@/components/work-orders/WorkOrderList'
import WorkOrderModal from '@/components/work-orders/WorkOrderModal'
import WorkOrderFilters from '@/components/work-orders/WorkOrderFilters'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import ErrorMessage from '@/components/common/ErrorMessage'

export default function WorkOrdersPage() {
  const currentUser = useSelector(selectUser)
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState({
    status: [] as string[],
    priority: [] as string[],
    assignedTo: [] as string[],
    equipmentType: [] as string[]
  })
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; workOrder: WorkOrder | null }>({
    isOpen: false,
    workOrder: null
  })

  // API hooks
  const { 
    data: workOrdersResponse, 
    isLoading: isLoadingWorkOrders, 
    error: workOrdersError,
    refetch: refetchWorkOrders
  } = useGetWorkOrdersQuery({
    ...filters,
    limit: 50
  })
  
  const { data: equipmentResponse } = useGetEquipmentQuery({ limit: 100 })
  const { data: usersResponse } = useGetUsersQuery()
  
  const [createWorkOrder, { isLoading: isCreating }] = useCreateWorkOrderMutation()
  const [updateWorkOrder, { isLoading: isUpdating }] = useUpdateWorkOrderMutation()
  const [deleteWorkOrder, { isLoading: isDeleting }] = useDeleteWorkOrderMutation()

  const workOrders = workOrdersResponse?.data || []
  const equipment = equipmentResponse?.data || []
  const users = usersResponse?.data || []

  // Filter work orders based on search term
  const filteredWorkOrders = workOrders.filter(workOrder =>
    workOrder.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    workOrder.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    workOrder.equipment?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleCreateWorkOrder = () => {
    setSelectedWorkOrder(null)
    setIsEditMode(false)
    setIsModalOpen(true)
  }

  const handleEditWorkOrder = (workOrder: WorkOrder) => {
    setSelectedWorkOrder(workOrder)
    setIsEditMode(true)
    setIsModalOpen(true)
  }

  const handleDeleteWorkOrder = (workOrder: WorkOrder) => {
    setDeleteConfirm({ isOpen: true, workOrder })
  }

  const handleSaveWorkOrder = async (workOrderData: Partial<WorkOrder>) => {
    try {
      if (isEditMode && selectedWorkOrder) {
        await updateWorkOrder({
          id: selectedWorkOrder.id,
          updates: workOrderData
        }).unwrap()
        toast.success('Work order updated successfully')
      } else {
        await createWorkOrder({
          ...workOrderData,
          createdBy: currentUser?.id || '',
          requestedBy: currentUser?.id || ''
        }).unwrap()
        toast.success('Work order created successfully')
      }
      setIsModalOpen(false)
      refetchWorkOrders()
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to save work order')
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.workOrder) return

    try {
      await deleteWorkOrder(deleteConfirm.workOrder.id).unwrap()
      toast.success('Work order deleted successfully')
      setDeleteConfirm({ isOpen: false, workOrder: null })
      refetchWorkOrders()
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to delete work order')
    }
  }

  const handleApplyFilters = (newFilters: typeof filters) => {
    setFilters(newFilters)
  }

  const clearFilters = () => {
    setFilters({
      status: [],
      priority: [],
      assignedTo: [],
      equipmentType: []
    })
    setSearchTerm('')
  }

  if (workOrdersError) {
    // Error displayed to user via ErrorMessage component
    // Only log in development for debugging
    if (import.meta.env.DEV) {
      console.debug('Work Orders API Error:', workOrdersError)
    }
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Work Orders</h1>
          <p className="text-lg text-gray-600">
            Manage and track maintenance work orders
          </p>
        </div>
        <ErrorMessage 
          message={`Failed to load work orders${
            workOrdersError && 'data' in workOrdersError && workOrdersError.data && 
            typeof workOrdersError.data === 'object' && 'message' in workOrdersError.data
              ? ': ' + (workOrdersError.data as any).message 
              : ''
          }`}
          onRetry={refetchWorkOrders}
        />
        {/* Debug info in development */}
        {import.meta.env.DEV && (
          <div className="bg-gray-100 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Debug Info:</h4>
            <pre className="text-xs text-gray-600 overflow-auto">
              {JSON.stringify(workOrdersError, null, 2)}
            </pre>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Work Orders</h1>
          <p className="text-lg text-gray-600">
            Manage and track maintenance work orders
          </p>
        </div>
        <button
          onClick={handleCreateWorkOrder}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Create Work Order
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search work orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
              showFilters
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="w-5 h-5" />
            Filters
          </button>
          {(filters.status.length > 0 || filters.priority.length > 0 || filters.assignedTo.length > 0 || filters.equipmentType.length > 0 || searchTerm) && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Clear All
            </button>
          )}
        </div>

        {showFilters && (
          <WorkOrderFilters
            filters={filters}
            onApplyFilters={handleApplyFilters}
            equipment={equipment}
            users={users}
          />
        )}
      </div>

      {/* Work Orders List */}
      {isLoadingWorkOrders ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <WorkOrderList
          workOrders={filteredWorkOrders}
          onEdit={handleEditWorkOrder}
          onDelete={handleDeleteWorkOrder}
          isLoading={isUpdating || isDeleting}
          equipment={equipment}
        />
      )}

      {/* Work Order Modal */}
      <WorkOrderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveWorkOrder}
        workOrder={selectedWorkOrder}
        equipment={equipment}
        users={users}
        isLoading={isCreating || isUpdating}
        isEditMode={isEditMode}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, workOrder: null })}
        onConfirm={handleConfirmDelete}
        title="Delete Work Order"
        message={`Are you sure you want to delete work order "${deleteConfirm.workOrder?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  )
}